<template>
    <div>

        <Card :header="$t('traffic')" overlay :loading="requestChart.getLoading()">

            <ErrorMessage :value="requestChart.getError()" />

            <template #nav>
                <div class="d-flex gap-3">
                    <b-dropdown :text="$t(`${currentChart}`)" toggle-class="caret wide">
                        <template v-for="chartType in ['impressions', 'clicks']">
                            <b-dropdown-item :key="chartType" :active="chartType === currentChart" @click="() => currentChart = chartType">{{ $t(`${chartType}`) }}</b-dropdown-item>
                        </template>
                    </b-dropdown>
                    <Multiselect
                        :fetch-options-function="fetchCampaigns"
                        :placeholder="$t('selectCampaign')"
                        searchable
                        track-by="id"
                        label="name"
                        select-label=""
                        :disabled="requestChart.getLoading()"
                        show-checkboxes
                        show-selected
                        multiple
                        :close-on-select="false"
                        :clear-on-select="false"
                        class="width-200"
                        @inputTrackBy="campaigns = $event"
                        @change="fetchChart()"
                    />
                    <FormSelectTree
                        ref="FormTreeSelect"
                        v-model="categories"
                        :disabled="requestChart.getLoading()"
                        :load-options="fetchCategories"
                        :normalizer="normalizer"
                        :placeholder="$t('category')"
                        multiple
                        class="width-200"
                        show-count-value
                        @change="fetchChart()"
                    />
                </div>
            </template>

            <div class="d-flex gap-4 flex-column">
                <Chart
                    :height="400"
                    :series="[]"
                    :options="chartOptions"
                    :range="groupBy"
                />
                <div class="d-flex gap-4 flex-wrap">
                    <Chart
                        :height="400"
                        :series="[]"
                        :options="chartOptions2"
                        :range="groupBy"
                        class="mb-0 width-min-400 flex-basis-0 flex-grow-10"
                    />
                    <Chart
                        :height="400"
                        :series="[]"
                        :options="chartOptions3"
                        :range="groupBy"
                        class="mb-0 width-min-400 flex-basis-0 flex-grow-1"
                    />
                </div>
            </div>

        </Card>
    </div>
</template>



<script>
    import { format } from 'date-fns';
    import { chartDataGroupBy, generateChartData } from '@/utils/chart';
    import Chart from '@/components/common/Chart.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import FormSelectTree from '@/components/common/FormSelectTree.vue';

    export default {
        name: 'ImpressionsReport',
        components: {
            Chart,
            Multiselect,
            FormSelectTree,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                requestChartImpressions: this.$req('space/CategoryImpressionsReport'),
                requestChartClicks: this.$req('space/CategoryClicksReport'),
                requestCampaigns: this.$req('buyboxes/AvailableCampaigns'),
                requestProductsTree: this.$req('space/ProductsTree'),
                campaigns: [],
                categories: [],
                formatDate: format,
                currentChart: 'impressions',
            };
        },
        computed: {
            requestChart() {
                if (this.currentChart === 'impressions') return this.requestChartImpressions;
                if (this.currentChart === 'clicks') return this.requestChartClicks;
                return null;
            },
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            timeRange() {
                return this.parentComponent.timeRange;
            },
            apiParams() {
                const urlParams = { spaceId: this.spaceId };
                const requestParams = { ...(this.timeRange || {}), groupBy: this.groupBy };
                if (this.campaigns?.length) requestParams.campaigns = this.campaigns.join(',');
                if (this.categories?.length) requestParams.categories = this.categories.join(',');
                return { urlParams, requestParams };
            },
            groupBy() {
                return chartDataGroupBy(this.timeRange);
            },
            chartData() {
                return this.requestChart.getData() || [];
            },
            chartSeries() {
                const chartData = (this.chartData?.chart || []).reduce((acc, item) => {
                    Object.keys(item).forEach(name => {
                        if (name !== 'date') {
                            if (!acc[name]) acc[name] = []; // eslint-disable-line no-param-reassign
                            acc[name].push([item.date, item[name]]);
                        }
                    });
                    return acc;
                }, {});
                return Object.keys(chartData).map(name => ({
                    name,
                    data: generateChartData(this.timeRange, chartData[name], this.groupBy),
                }));
            },
            chartOptions() {
                return {
                    series: this.chartSeries,
                };
            },
            chartOptions2() {
                return {
                    series: this.chartSeries,
                    chart: {
                        type: 'bar',
                        stacked: true,
                        stackType: '100%',
                    },
                    title: {
                        text: this.$t('percentageShare'),
                        align: 'center',
                    },
                    yaxis: {
                        labels: {
                            formatter: val => this.$format.percent(val, 0),
                        },
                    },
                    tooltip: {
                        y: {
                            formatter: val => val,
                        },
                    },
                    dataLabels: {
                        enabled: true,
                        formatter: val => this.$format.percent(val, 0),
                    },
                };
            },
            chartOptions3() {
                return {
                    series: (this.chartData?.pie_chart || []).map(([name, value]) => value),
                    labels: (this.chartData?.pie_chart || []).map(([name, value]) => name),
                    chart: {
                        type: 'pie',
                    },
                    title: {
                        text: this.$t('percentageShareSum'),
                        align: 'center',
                    },
                };
            },
        },
        watch: {
            spaceId: {
                handler: 'loadData',
            },
            timeRange: {
                handler: 'loadData',
            },
            currentChart: {
                handler: 'loadData',
            },
        },
        methods: {
            loadData() {
                this.fetchChart();
            },
            fetchChart() {
                this.requestChart.get(this.apiParams);
            },
            fetchCampaigns() {
                const id = this.$store.getters['plugin/getProducerPlugin'].id;
                return this.requestCampaigns.get({ urlParams: { id, v: 1 } });
            },
            fetchProductsTree(categoryId) {
                return this.requestProductsTree.get({ urlParams: { spaceId: this.spaceId }, requestParams: { category: categoryId } });
            },
            fetchCategories({ parentNode }) {
                return this.fetchProductsTree(parentNode?.entityId);
            },
            normalizer(node) {
                return {
                    id: node.entityId,
                    label: node.text,
                    children: node.children === true ? null : node.children,
                    isDisabled: node.type === 'product',
                };
            },
        },
        created() {
            this.loadData();
        },
    };
</script>



<style lang="scss" scoped></style>
